import { useEffect, useState, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

export default function PaymentSuccessPage() {
  const query = useQuery();
  const sessionId = query.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, error: null });
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState({ status: "error", data: null, error: "Missing session_id" });
      return;
    }
    try {
      const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({ status: "paid", data, error: null });
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired", data, error: null });
        return;
      }
      setAttempts((a) => a + 1);
    } catch (err) {
      // Stripe sometimes 503s for a few seconds after redirect — keep polling
      // until MAX_POLLS rather than failing on the first transient error.
      const status = err?.response?.status;
      if (status === 404) {
        setState({
          status: "error",
          data: null,
          error: "We couldn't find this checkout session. If you were charged, contact support.",
        });
        return;
      }
      setAttempts((a) => a + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (state.status !== "polling") return;
    if (attempts >= MAX_POLLS) return;
    // Defer to next microtask so setAttempts/setState inside poll never run
    // synchronously inside the effect body (react-hooks/set-state-in-effect).
    const handle = setTimeout(poll, 0);
    const t = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      clearTimeout(handle);
      clearTimeout(t);
    };
  }, [attempts, poll, state.status]);

  // Derive the terminal "timeout" state from attempts rather than calling
  // setState inside the polling effect (avoids react-hooks/set-state-in-effect).
  const displayStatus =
    state.status === "polling" && attempts >= MAX_POLLS ? "timeout" : state.status;
  const displayError =
    displayStatus === "timeout"
      ? "Payment verification timed out. If you were charged, contact support with your session id."
      : state.error;

  const submissionId = state.data?.submission_id;

  // Anonymous flow: only the submission link exists. Send the user back to it.
  useEffect(() => {
    if (state.status !== "paid") return;
    const t = setTimeout(() => {
      if (submissionId) navigate(`/results/${submissionId}`);
      else navigate("/");
    }, 800);
    return () => clearTimeout(t);
  }, [state.status, submissionId, navigate]);

  return (
    <div className="min-h-screen bg-slate-50/40">
      <SiteHeader />
      <main className="max-w-md mx-auto px-6 py-20" data-testid="payment-success-main">
        <Card className="border-slate-200 p-8 text-center">
          {displayStatus === "polling" && (
            <div data-testid="payment-polling">
              <Loader2 className="w-10 h-10 mx-auto text-slate-400 animate-spin mb-4" />
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-2">
                Verifying your payment…
              </h1>
              <p className="text-sm text-slate-500">This usually takes a few seconds.</p>
            </div>
          )}

          {displayStatus === "paid" && (
            <div data-testid="payment-paid">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-[#065F46]" />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-2">
                Payment successful
              </h1>
              <p className="text-sm text-slate-600 mb-6">
                Thanks! Your analysis is unlocked.
              </p>
              {submissionId ? (
                <Button
                  data-testid="go-to-results-button"
                  onClick={() => navigate(`/results/${submissionId}`)}
                  className="w-full bg-[#065F46] hover:bg-[#047857] text-white"
                >
                  View my swing analysis
                </Button>
              ) : (
                <Button
                  data-testid="go-home-button"
                  onClick={() => navigate("/")}
                  className="w-full bg-[#065F46] hover:bg-[#047857] text-white"
                >
                  Back to home
                </Button>
              )}
            </div>
          )}

          {(displayStatus === "expired" || displayStatus === "error" || displayStatus === "timeout") && (
            <div data-testid="payment-failed">
              <div className="w-14 h-14 rounded-full bg-rose-100 mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-rose-600" />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-2">
                Payment {displayStatus === "expired" ? "expired" : "not confirmed"}
              </h1>
              <p className="text-sm text-slate-600 mb-6">
                {displayError || "We couldn't confirm your payment. You haven't been charged if the session expired."}
              </p>
              <Link to="/">
                <Button variant="outline" className="w-full">Back home</Button>
              </Link>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
